/**
 * RulesManagementView — Full UI for the Functional Rules Framework.
 *
 * Sections:
 *  1. DB connection config (for rule execution target)
 *  2. Rule list for the current project
 *  3. Rule authoring (structured form — NL parse comes next sprint)
 *  4. Rule execution + results (RuleResultsList)
 *  5. Library: browse, reuse
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Check,
  ChevronLeft,
  Code2,
  Database,
  ListChecks,
  Loader2,
  Play,
  Plus,
  Send,
  ShieldAlert,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  rulesApi,
  type LibraryEntry,
  type Rule,
  type RuleResult,
  type RuleType,
  type SchemaColumn,
  describeSourceConnection,
} from "../api/rules";
import { useAuth } from "../auth";
import { useProjectRuleAccess, useRulesGatesFor } from "../hooks/useRulesGates";
import {
  canOverride,
  fourEyesConflicts,
  fourEyesReason,
  needsConfirmation,
  shortDate,
} from "../lib/ruleGovernance";
import { ReviewDialog, type ReviewKind } from "./rules/ReviewDialog";
import { KnowledgePacksPanel } from "./rules/KnowledgePacksPanel";
import type { ActionGate } from "../lib/rulesPermissions";
import { RuleResultsList } from "./rules/RuleResultsList";
import { RuleLibraryGallery } from "./rules/RuleLibraryGallery";
import { CompiledSqlPreview } from "./rules/CompiledSqlPreview";
import {
  RuleCodePanel,
  type RuleCode,
  type RuleCodeChange,
} from "./rules/RuleCodePanel";
import {
  GatedButton,
  RulesPermissionNotice,
} from "./rules/RulesPermissionNotice";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RulePrefillDraft {
  rule_type: RuleType;
  label: string;
  description: string;
  expression: Record<string, unknown>;
  /**
   * When set, the promoted rule is authored as raw SQL (condition_sql) rather
   * than the structured DSL — so it executes immediately via the existing
   * evaluator (the structured-DSL executor is not built yet). Promote-to-rule
   * emits this; the create form shows an editable SQL box. Use ``{schema}`` for
   * the source schema (rendered at eval time).
   */
  condition_sql?: string;
  /** The table this promoted finding targets, when known (e.g. from a
   * Findings-tab violation, which is always table-scoped). */
  table_name?: string;
}

export interface RulesManagementViewProps {
  projectId: string;
  /**
   * The saved project connection to run rules against (the Data Profiler passes the
   * connection it profiled). Omitted: the server uses the project's newest active one.
   */
  connectionId?: string;
  /** "file" when the profiled source is a file / folder (rules run in DuckDB). */
  sourceKind?: "database" | "file";
  onBack: () => void;
  /**
   * When rendered inside the Data Profiler's Rules tab, drop the top-level
   * page header (title + breadcrumb + back chevron) to avoid visual
   * duplication. The body (DB config + tabs + rules list) still renders.
   */
  embedded?: boolean;
  /**
   * Days 9-10: seed the rule create form with a draft template (typically
   * produced by Findings → "Promote to rule"). When set, the form auto-opens
   * with label / description / structured_expression prefilled so the user
   * lands directly in authoring.
   */
  prefillDraft?: RulePrefillDraft | null;
  /** Called once the parent's prefillDraft has been consumed and cleared. */
  onPrefillConsumed?: () => void;
  /** Schema-aware placeholder text for the New Rule form's Describe/Label
   * fields — passed down from the embedding page (e.g. Data Profiler) when
   * a real profiled schema is available. See RuleCreateForm. */
  describeExample?: string;
  labelExample?: string;
  /** Schema-aware placeholder for the Raw SQL tab's Violation SQL field. */
  violationSqlExample?: string;
  /** Schema-aware initial value for the Structured JSON tab's expression
   * editor (replaces the generic SAMPLE_DQ6 template when available). */
  structuredExpressionExample?: string;
  /** Real profiled schema (table → columns), sent as the parse-nl request's
   * schema_context so the "Describe in English" tab resolves column
   * references against the actual connected project instead of the
   * hardcoded FSA/NSLDS loans/borrowers schema below. */
  schemaContext?: { tables: Record<string, SchemaColumn[]> };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const NO_PROJECT_REASON =
  "This page is not attached to a project, so there is no saved source connection to run rules against.";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Viewing a rule's code needs nothing beyond seeing the rule.
const VIEW_GATE: ActionGate = {
  allowed: true,
  permission: "rules:read",
  reason: null,
};

function ruleCode(r: Rule): RuleCode {
  return {
    key: r.id,
    label: r.label,
    status: r.status,
    origin: "project",
    expr_kind: r.expr_kind,
    condition_sql: r.condition_sql,
    population_sql: r.population_sql,
    structured_expression: r.structured_expression,
    table_name: r.source_tables[0] ?? null,
    severity: r.severity,
    category: r.rule_type,
    derived_from: r.derived_from,
  };
}

function libraryCode(e: LibraryEntry): RuleCode {
  return {
    key: e.id,
    label: e.label,
    origin: "knowledge_base",
    expr_kind: e.expr_kind,
    condition_sql: e.condition_sql,
    population_sql: e.population_sql,
    structured_expression: e.structured_expression,
    table_name: e.table_name,
    severity: e.severity,
    category: e.rule_type,
  };
}

export default function RulesManagementView({
  projectId,
  connectionId,
  sourceKind,
  onBack,
  embedded = false,
  prefillDraft = null,
  onPrefillConsumed,
  describeExample,
  labelExample,
  violationSqlExample,
  structuredExpressionExample,
  schemaContext,
}: RulesManagementViewProps) {
  const navigate = useNavigate();
  // The standalone Rules page binds to a label ("standalone-<user id>") when the user
  // has no project: rules can be authored there, but there is no saved source
  // connection to run them against.
  const noProject = !UUID_RE.test(projectId);
  // What this user's role may do with rules (rules:execute / create / update /
  // approve). The backend enforces the same codenames; this only explains.
  const accessState = useProjectRuleAccess(projectId);
  const access = accessState.access;
  const gates = useRulesGatesFor(accessState);
  const { user } = useAuth();
  const myId = access?.user_id ?? user?.id ?? null;
  const isPlatformAdmin = Boolean(access?.is_platform_admin);
  // Display names for the people in a rule's governance facts (project members; others show as their id).
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (noProject) return;
    let cancelled = false;
    rulesApi.projectRoles
      .list(projectId)
      .then((r) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        r.members.forEach((m) => {
          map[m.user_id] = m.user_name || m.user_email;
        });
        setNames(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId, noProject]);
  const nameOf = useCallback(
    (id: string | null | undefined) =>
      !id ? "" : id === myId ? "you" : (names[id] ?? id),
    [names, myId],
  );
  // Approve / reject are closed, with the four-eyes reason, on a rule the user may not review.
  const reviewGate = useCallback(
    (rule: Rule): ActionGate => {
      const reason = fourEyesReason(
        fourEyesConflicts(rule, myId),
        isPlatformAdmin,
      );
      if (!gates.approve.allowed || !reason) return gates.approve;
      return { ...gates.approve, allowed: false, reason };
    },
    [gates.approve, myId, isPlatformAdmin],
  );
  const [review, setReview] = useState<{ kind: ReviewKind; rule: Rule } | null>(
    null,
  );
  const [reviewBusy, setReviewBusy] = useState(false);
  // Running needs BOTH the permission and a project with a saved source connection.
  // Either reason closes the control, and when both apply both are stated.
  const runGate = useMemo<ActionGate>(() => {
    if (!noProject) return gates.run;
    return {
      ...gates.run,
      allowed: false,
      reason: gates.run.allowed
        ? NO_PROJECT_REASON
        : `${gates.run.reason} ${NO_PROJECT_REASON}`,
    };
  }, [gates.run, noProject]);

  // ── data state
  const [rules, setRules] = useState<Rule[]>([]);
  const [library, setLibrary] = useState<LibraryEntry[]>([]);
  const [results, setResults] = useState<RuleResult[]>([]);
  const [sourceDesc, setSourceDesc] = useState<string>("");

  // ── ui state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executing, setExecuting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState<"rules" | "library">("rules");
  const [showCreateForm, setShowCreateForm] = useState(false);
  // The rule whose code is open: a project rule (editable) or a pack rule.
  const [codeView, setCodeView] = useState<
    | { kind: "rule"; rule: Rule }
    | { kind: "library"; entry: LibraryEntry }
    | null
  >(null);
  // The SQL tab of a structured rule: its JSON compiled for the project's
  // source (undefined on the standalone page, which has no project).
  const compileRuleSql = noProject
    ? undefined
    : (structured_expression: Record<string, unknown>) =>
        rulesApi.compileSql({
          structured_expression,
          project_id: projectId,
          ...(connectionId ? { connection_id: connectionId } : {}),
          ...(sourceKind ? { source_kind: sourceKind } : {}),
        });

  // ── derived
  const ruleLabels = useMemo(() => {
    const map: Record<string, string> = {};
    rules.forEach((r) => {
      map[r.id] = r.label;
    });
    return map;
  }, [rules]);

  // ── data loaders
  const loadRules = useCallback(async () => {
    const res = await rulesApi.list({ project_id: projectId });
    setRules(res.rules.filter((r) => r.status !== "retired"));
  }, [projectId]);

  const loadLibrary = useCallback(async () => {
    const res = await rulesApi.library.list({ project_id: projectId });
    setLibrary(res.entries);
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        await Promise.all([loadRules(), loadLibrary()]);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRules, loadLibrary]);

  // Auto-open the create form + land on My Rules when Findings hands us a
  // prefilled draft (Days 9-10 "Promote to rule").
  useEffect(() => {
    if (prefillDraft) {
      setActiveTab("rules");
      setShowCreateForm(true);
    }
  }, [prefillDraft]);

  // ── actions
  const runAllRules = useCallback(async () => {
    if (rules.length === 0) {
      setError("No rules to execute. Create a rule first.");
      return;
    }
    try {
      setExecuting(true);
      setError(null);
      // No connection details are sent: the server runs the rules against the
      // project's saved source connection (Project Setup), never a caller-supplied host.
      const res = await rulesApi.executeBatch({
        project_id: projectId,
        ...(connectionId ? { connection_id: connectionId } : {}),
      });
      setResults(res.results);
      setSourceDesc(
        describeSourceConnection(res.source_connection) ||
          res.source_description,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExecuting(false);
    }
  }, [rules, projectId, connectionId]);

  const runSingleRule = useCallback(
    async (rule: Rule) => {
      try {
        setExecuting(true);
        setError(null);
        const res = await rulesApi.executeDirect(rule.id, {
          project_id: projectId,
          connection_id: connectionId,
        });
        // Add/replace the row in results
        setResults((prev) => {
          const without = prev.filter((r) => r.rule_id !== rule.id);
          return [res, ...without];
        });
        setSourceDesc(
          describeSourceConnection(res.source_connection) ||
            res.source_description,
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setExecuting(false);
      }
    },
    [projectId, connectionId],
  );

  // Save edited rule code (PUT; the server re-validates the SQL and sends a
  // reviewed rule back to draft), then optionally run the saved rule.
  const saveRuleCode = useCallback(
    async (rule: Rule, change: RuleCodeChange | null, run: boolean) => {
      let saved = rule;
      if (change) {
        saved = await rulesApi.update(rule.id, change);
        // The save succeeded; a failed list refresh must not read as a failed save.
        loadRules().catch(() => {});
      }
      if (run) {
        setCodeView(null);
        await runSingleRule(saved);
      } else {
        // Only if the panel is still open on this rule (it may have been closed
        // while the save was in flight).
        setCodeView((cur) =>
          cur?.kind === "rule" && cur.rule.id === rule.id
            ? { kind: "rule", rule: saved }
            : cur,
        );
      }
    },
    [loadRules, runSingleRule],
  );

  // A pack rule is read-only: copy it into the project, then open the copy's
  // code for editing.
  const reuseToEdit = useCallback(
    async (entry: LibraryEntry) => {
      const copy = await rulesApi.library.reuse(entry.id, {
        target_project_id: projectId,
      });
      await Promise.all([loadRules(), loadLibrary()]);
      setActiveTab("rules");
      setCodeView({ kind: "rule", rule: copy });
    },
    [projectId, loadRules, loadLibrary],
  );

  const deleteRule = useCallback(
    async (rule: Rule) => {
      if (
        !confirm(
          `Retire rule "${rule.label}"? It stops counting in profiling runs; the rule and its approval history are kept.`,
        )
      )
        return;
      try {
        await rulesApi.delete(rule.id);
        await loadRules();
        setResults((prev) => prev.filter((r) => r.rule_id !== rule.id));
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [loadRules],
  );

  const exportFailingToBacklog = useCallback(async () => {
    const failingIds = results
      .filter((r) => r.status === "fail")
      .map((r) => r.rule_id);
    if (failingIds.length === 0) {
      setError(
        "No failing rules to export. Run rules first, then export findings.",
      );
      return;
    }
    try {
      setExporting(true);
      setError(null);
      const res = await rulesApi.exportToBacklog({
        project_id: projectId,
        rule_ids: failingIds,
      });
      // Navigate to the Scrum Planner to let user refine the auto-generated backlog
      navigate(
        `/projects/${projectId}/agents/backlog-architect?session=${res.session_id}`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }, [results, projectId, navigate]);

  // ── approval workflow (Days 5-6)
  const submitForApproval = useCallback((rule: Rule) => {
    setReview({ kind: "submit", rule });
  }, []);

  const approveRule = useCallback((rule: Rule) => {
    setReview({ kind: needsConfirmation(rule) ? "confirm" : "approve", rule });
  }, []);

  const rejectRule = useCallback((rule: Rule) => {
    setReview({ kind: "reject", rule });
  }, []);

  const overrideRule = useCallback((rule: Rule) => {
    setReview({ kind: "override", rule });
  }, []);

  const [reviewError, setReviewError] = useState<string | null>(null);
  const closeReview = useCallback(() => {
    if (reviewBusy) return; // never close mid-request: the decision may still commit
    setReview(null);
    setReviewError(null);
  }, [reviewBusy]);

  const submitReview = useCallback(
    async (text: string, ticketRef: string) => {
      if (!review) return;
      setReviewBusy(true);
      setReviewError(null);
      try {
        const { kind, rule } = review;
        if (kind === "submit") await rulesApi.approvals.submit(rule.id, text);
        else if (kind === "reject")
          await rulesApi.approvals.reject(rule.id, text);
        else if (kind === "override")
          await rulesApi.approvals.override(rule.id, text, ticketRef);
        else await rulesApi.approvals.approve(rule.id, text);
        setReview(null);
        await loadRules();
      } catch (err) {
        // Keep the dialog (and the typed reason / justification) open, with the server's answer in it.
        setReviewError(err instanceof Error ? err.message : String(err));
      } finally {
        setReviewBusy(false);
      }
    },
    [review, loadRules],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      className={
        embedded ? "space-y-6" : "max-w-7xl mx-auto px-4 py-6 space-y-6"
      }
    >
      {/* Header — suppressed when embedded inside Data Profiler */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="text-white/50 hover:text-white/80 transition-colors"
              aria-label="Back"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-white/95">
                Data Quality Rules
              </h1>
              <p className="text-sm text-white/50">
                Author business rules and execute them against your data source.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-white/15 bg-white/5 text-white/70"
              title="Rules run against the source connection saved on this project (Project Setup)"
            >
              <Database className="w-3.5 h-3.5" />
              {sourceDesc || "Project's saved source connection"}
            </span>
            <GatedButton
              gate={runGate}
              onClick={runAllRules}
              disabled={executing || rules.length === 0}
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-blue-500/80 hover:bg-blue-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {executing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              Run All Rules
            </GatedButton>
            <button
              onClick={exportFailingToBacklog}
              disabled={
                exporting ||
                results.filter((r) => r.status === "fail").length === 0
              }
              className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-emerald-500/80 hover:bg-emerald-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
              title="Generate a Scrum Planner backlog from failing rules"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ListChecks className="w-4 h-4" />
              )}
              Export Findings to Backlog
            </button>
          </div>
        </div>
      )}

      {noProject && (
        // role="status", not "note": the permission notice below is the page's "note" and the two
        // explain different things (no project here vs no permission), so neither replaces the other.
        <div
          role="status"
          className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 text-sm text-amber-100"
        >
          {NO_PROJECT_REASON} Rules can be authored here; open them from a
          project (Project Setup → Source database) to run them.
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="rounded-xl border border-red-400/40 bg-red-400/10 p-3 text-sm text-red-200 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-200 hover:text-red-100"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Which rule actions this role cannot use, and the permission that unlocks each */}
      <RulesPermissionNotice gates={gates} />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/10">
        <TabButton
          active={activeTab === "rules"}
          onClick={() => setActiveTab("rules")}
          count={rules.length}
        >
          My Rules
        </TabButton>
        <TabButton
          active={activeTab === "library"}
          onClick={() => setActiveTab("library")}
          count={library.length}
        >
          Rule Library
        </TabButton>
      </div>

      {/* Tab content */}
      {loading ? (
        <div className="text-center text-white/50 py-12">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
          Loading rules…
        </div>
      ) : activeTab === "rules" ? (
        <div className="grid lg:grid-cols-[1fr_2fr] gap-6 items-start">
          {/* Left: rule list */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white/80 uppercase tracking-wider">
                Rules ({rules.length})
              </h2>
              <GatedButton
                gate={gates.create}
                onClick={() => setShowCreateForm((v) => !v)}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 text-white/80"
              >
                <Plus className="w-3 h-3" />
                Add Rule
              </GatedButton>
            </div>
            {showCreateForm && (
              <RuleCreateForm
                projectId={projectId}
                onCreated={async () => {
                  setShowCreateForm(false);
                  await loadRules();
                  onPrefillConsumed?.();
                }}
                onCancel={() => {
                  setShowCreateForm(false);
                  onPrefillConsumed?.();
                }}
                prefill={prefillDraft}
                connectionId={connectionId}
                sourceKind={sourceKind}
                createGate={gates.create}
                nlGate={gates.nl}
                describeExample={describeExample}
                labelExample={labelExample}
                violationSqlExample={violationSqlExample}
                structuredExpressionExample={structuredExpressionExample}
                schemaContext={schemaContext}
              />
            )}
            {rules.length === 0 && !showCreateForm ? (
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center text-sm text-white/50">
                No rules yet. Click <strong>Add Rule</strong> or reuse one from
                the{" "}
                <button
                  onClick={() => setActiveTab("library")}
                  className="text-blue-300 hover:text-blue-200 underline"
                >
                  Rule Library
                </button>
                .
              </div>
            ) : (
              <div className="space-y-2">
                {rules.map((r) => (
                  <div
                    key={r.id}
                    className="rounded-lg border border-white/10 bg-white/5 p-3 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="text-sm text-white/90 font-medium leading-snug">
                        {r.label}
                      </div>
                      <div className="flex items-center gap-1">
                        {(r.status === "draft" ||
                          r.status === "validated" ||
                          r.status === "rejected") && (
                          <IconAction
                            gate={gates.update}
                            title="Submit for approval"
                            hoverClass="hover:text-blue-300"
                            onClick={() => submitForApproval(r)}
                          >
                            <Send className="w-3.5 h-3.5" />
                          </IconAction>
                        )}
                        {r.status === "submitted" && (
                          <>
                            <IconAction
                              gate={reviewGate(r)}
                              title="Approve"
                              hoverClass="hover:text-emerald-300"
                              onClick={() => approveRule(r)}
                            >
                              <ThumbsUp className="w-3.5 h-3.5" />
                            </IconAction>
                            <IconAction
                              gate={reviewGate(r)}
                              title="Reject"
                              hoverClass="hover:text-red-400"
                              onClick={() => rejectRule(r)}
                            >
                              <ThumbsDown className="w-3.5 h-3.5" />
                            </IconAction>
                          </>
                        )}
                        {needsConfirmation(r) && (
                          <IconAction
                            gate={reviewGate(r)}
                            title="Confirm approval"
                            hoverClass="hover:text-emerald-300"
                            onClick={() => approveRule(r)}
                          >
                            <ThumbsUp className="w-3.5 h-3.5" />
                          </IconAction>
                        )}
                        {isPlatformAdmin && r.governance && canOverride(r) && (
                          <IconAction
                            gate={gates.approve}
                            title="Approve by override"
                            hoverClass="hover:text-amber-300"
                            onClick={() => overrideRule(r)}
                          >
                            <ShieldAlert className="w-3.5 h-3.5" />
                          </IconAction>
                        )}
                        <IconAction
                          gate={VIEW_GATE}
                          title="View / edit code"
                          hoverClass="hover:text-blue-300"
                          onClick={() => setCodeView({ kind: "rule", rule: r })}
                        >
                          <Code2 className="w-3.5 h-3.5" />
                        </IconAction>
                        <IconAction
                          gate={runGate}
                          title="Run this rule"
                          hoverClass="hover:text-emerald-300"
                          busy={executing}
                          onClick={() => runSingleRule(r)}
                        >
                          <Play className="w-3.5 h-3.5" />
                        </IconAction>
                        <IconAction
                          gate={gates.retire}
                          title="Retire"
                          hoverClass="hover:text-red-400"
                          onClick={() => deleteRule(r)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </IconAction>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/50">
                      <StatusPill status={r.status} />
                      <GovernanceNote rule={r} nameOf={nameOf} />
                      <span className="px-1.5 py-0.5 rounded bg-white/10 text-white/70 uppercase tracking-wider text-[11px]">
                        {r.rule_type.replace(/_/g, " ")}
                      </span>
                      <span className="text-white/30">·</span>
                      <span>{r.source_tables.join(", ") || "no tables"}</span>
                      {r.is_cross_table && (
                        <>
                          <span className="text-white/30">·</span>
                          <span className="text-amber-300/80">cross-table</span>
                        </>
                      )}
                    </div>
                    {r.description && (
                      <div className="mt-2 text-xs text-white/50 leading-relaxed">
                        {r.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: execution results */}
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-white/80 uppercase tracking-wider">
              Execution Results
            </h2>
            <RuleResultsList
              results={results}
              ruleLabels={ruleLabels}
              sourceDescription={sourceDesc || undefined}
              exportToBacklogEnabled={true}
              onExportToBacklog={async (r) => {
                try {
                  setExporting(true);
                  setError(null);
                  const res = await rulesApi.exportToBacklog({
                    project_id: projectId,
                    rule_ids: [r.rule_id],
                  });
                  navigate(
                    `/projects/${projectId}/agents/backlog-architect?session=${res.session_id}`,
                  );
                } catch (err) {
                  setError(err instanceof Error ? err.message : String(err));
                } finally {
                  setExporting(false);
                }
              }}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {!noProject && (
            <KnowledgePacksPanel
              projectId={projectId}
              adoptGate={gates.adopt}
              nameOf={nameOf}
            />
          )}
          <RuleLibraryGallery
            entries={library}
            targetProjectId={projectId}
            reuseDisabledReason={gates.create.reason}
            onReused={async () => {
              await Promise.all([loadRules(), loadLibrary()]);
              setActiveTab("rules");
            }}
            onViewCode={(entry) => setCodeView({ kind: "library", entry })}
          />
        </div>
      )}

      {review && (
        <ReviewDialog
          kind={review.kind}
          ruleLabel={review.rule.label}
          busy={reviewBusy}
          error={reviewError}
          onCancel={closeReview}
          onSubmit={(text, ticket) => void submitReview(text, ticket)}
        />
      )}
      {codeView?.kind === "rule" && (
        <RuleCodePanel
          key={codeView.rule.id}
          rule={ruleCode(codeView.rule)}
          editable={codeView.rule.editable !== false}
          editGate={gates.update}
          runGate={runGate}
          onSave={(change, run) => saveRuleCode(codeView.rule, change, run)}
          onClose={() => setCodeView(null)}
          compileSql={compileRuleSql}
        />
      )}
      {codeView?.kind === "library" && (
        <RuleCodePanel
          key={codeView.entry.id}
          rule={libraryCode(codeView.entry)}
          editable={false}
          editGate={gates.update}
          runGate={runGate}
          onSave={async () => {}}
          onClose={() => setCodeView(null)}
          compileSql={compileRuleSql}
          onReuseToEdit={() => reuseToEdit(codeView.entry)}
          reuseGate={
            noProject
              ? { ...gates.create, allowed: false, reason: NO_PROJECT_REASON }
              : gates.create
          }
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * A small icon button on a rule row. Closed gate: rendered disabled, with the
 * "Requires the rules:... permission" tooltip (on the wrapper, so it also shows
 * on a disabled button) and an accessible name that says why.
 */
function IconAction({
  gate,
  title,
  hoverClass,
  busy = false,
  onClick,
  children,
}: {
  gate: ActionGate;
  title: string;
  hoverClass: string;
  busy?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <GatedButton
      gate={gate}
      onClick={onClick}
      disabled={busy}
      ariaLabel={title}
      title={title}
      className={`text-white/40 ${hoverClass} disabled:opacity-40 disabled:cursor-not-allowed aria-disabled:hover:text-white/40`}
    >
      {children}
    </GatedButton>
  );
}

function TabButton({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm border-b-2 transition-colors ${
        active
          ? "border-blue-400 text-white"
          : "border-transparent text-white/50 hover:text-white/70"
      }`}
    >
      {children}
      <span
        className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
          active ? "bg-blue-400/20 text-blue-200" : "bg-white/10 text-white/50"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

/** Who approved / rejected / submitted the rule, and its override or pre-governance marker. */
function GovernanceNote({
  rule,
  nameOf,
}: {
  rule: Rule;
  nameOf: (id: string | null | undefined) => string;
}) {
  const g = rule.governance;
  if (!g) return null;
  const when = (iso: string | null) => (iso ? ` · ${shortDate(iso)}` : "");
  // What happened to THIS version comes first; a rule being edited or re-reviewed then says that its previously
  // approved version is still the one profiling runs use.
  const inForce =
    rule.status !== "approved" && g.approved_fingerprint ? (
      <span className="text-white/50">(approved version still in force)</span>
    ) : null;
  if (rule.status === "rejected") {
    return (
      <>
        <span
          className="text-red-200/80"
          title={g.rejection_reason ?? undefined}
        >
          Rejected by {nameOf(g.rejected_by)}
          {when(g.rejected_at)}
          {g.rejection_reason ? `: ${g.rejection_reason}` : ""}
        </span>
        {inForce}
      </>
    );
  }
  if (rule.status === "submitted") {
    return (
      <>
        <span className="text-amber-200/80">
          Submitted by {nameOf(g.submitted_by)}
          {when(g.submitted_at)}
        </span>
        {inForce}
      </>
    );
  }
  if (rule.status !== "approved") return inForce;
  if (g.approval_kind === "override") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] bg-amber-400/15 text-amber-200">
        <ShieldAlert className="w-3 h-3" aria-hidden />
        Approved by override · {nameOf(g.approved_by)}
        {when(g.approved_at)}
      </span>
    );
  }
  if (g.approval_kind === "pre_governance") {
    return (
      <span className="px-1.5 py-0.5 rounded text-[11px] bg-white/10 text-white/60">
        Approved before governance
      </span>
    );
  }
  return (
    <span className="text-emerald-200/80">
      Approved by {nameOf(g.approved_by)}
      {when(g.approved_at)}
    </span>
  );
}

function StatusPill({ status }: { status: Rule["status"] }) {
  const cfg: Record<Rule["status"], { cls: string; label: string }> = {
    draft: { cls: "bg-white/10 text-white/70", label: "draft" },
    validated: { cls: "bg-sky-400/15 text-sky-200", label: "validated" },
    submitted: {
      cls: "bg-amber-400/15 text-amber-200",
      label: "pending review",
    },
    approved: { cls: "bg-emerald-400/15 text-emerald-200", label: "approved" },
    rejected: { cls: "bg-red-400/15 text-red-200", label: "rejected" },
    retired: { cls: "bg-white/5 text-white/40", label: "retired" },
  };
  const { cls, label } = cfg[status] ?? cfg.draft;
  return (
    <span
      className={`px-1.5 py-0.5 rounded uppercase tracking-wider text-[11px] ${cls}`}
    >
      {label}
    </span>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider text-white/50 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Rule create form — simple MVP (structured JSON only; NL wizard is next sprint)
// ---------------------------------------------------------------------------

const SAMPLE_DQ6 = JSON.stringify(
  {
    rule_type: "conditional",
    when_conditions: [
      {
        column: { table: "loans", column: "loan_status" },
        operator: "in",
        value: ["RP", "FB", "IG", "DA", "DL"],
      },
    ],
    then_conditions: [
      {
        column: { table: "loans", column: "outstanding_principal_balance" },
        operator: "gt",
        value: 0,
      },
    ],
  },
  null,
  2,
);

/** The standalone Rules page binds to a label, not a project: nothing to
 * compile against. */
function noProjectId(projectId: string): boolean {
  return !UUID_RE.test(projectId);
}

function parseJsonObject(text: string): Record<string, unknown> {
  const parsed: unknown = JSON.parse(text);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
    throw new Error("the structured expression must be a JSON object");
  return parsed as Record<string, unknown>;
}

function RuleCreateForm({
  projectId,
  connectionId,
  sourceKind,
  onCreated,
  onCancel,
  prefill,
  createGate,
  nlGate,
  describeExample,
  labelExample,
  violationSqlExample,
  structuredExpressionExample,
  schemaContext,
}: {
  projectId: string;
  /** The source connection the rule runs against: the dialect SQL is drafted
   * and shown in. Default: the project's own. */
  connectionId?: string;
  sourceKind?: "database" | "file";
  onCreated: () => void;
  onCancel: () => void;
  prefill?: RulePrefillDraft | null;
  /** rules:create -- saving the rule. The form can still be open (a Findings
   * "Promote to rule" opens it); the save button then says why it is off. */
  createGate: ActionGate;
  /** rules:execute -- "Describe in English" calls parse-nl. */
  nlGate: ActionGate;
  /** Schema-aware placeholder text (real table/column names) for the
   * "Describe the rule" / "Label" fields, when a profiled schema is
   * available (e.g. embedded in the Data Profiler's Rules tab). Falls back
   * to generic examples when this form is used standalone. */
  describeExample?: string;
  labelExample?: string;
  /** Schema-aware placeholder for the Raw SQL tab's Violation SQL field. */
  violationSqlExample?: string;
  /** Schema-aware initial value for the Structured JSON expression editor. */
  structuredExpressionExample?: string;
  /** Real profiled schema sent as parse-nl's schema_context. Falls back to
   * the hardcoded FSA/NSLDS schema below when this form is used standalone
   * (no profiled session available). */
  schemaContext?: { tables: Record<string, SchemaColumn[]> };
}) {
  const [label, setLabel] = useState(prefill?.label ?? "");
  const [description, setDescription] = useState(prefill?.description ?? "");
  const [ruleType, setRuleType] = useState<RuleType>(
    prefill?.rule_type ?? "conditional",
  );
  const [exprText, setExprText] = useState(
    prefill
      ? JSON.stringify(prefill.expression, null, 2)
      : (structuredExpressionExample ?? SAMPLE_DQ6),
  );
  // Raw-SQL authoring (condition_sql). Promote-to-rule emits this so the rule
  // runs via the existing SQL evaluator without the (unbuilt) structured-DSL
  // executor. Use {schema} for the source schema.
  const [conditionSql, setConditionSql] = useState(
    prefill?.condition_sql ?? "",
  );
  const [tableName, setTableName] = useState(prefill?.table_name ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Authoring mode: structured JSON form, plain-English NL, or raw SQL.
  const [mode, setMode] = useState<"form" | "nl" | "sql">(
    prefill?.condition_sql ? "sql" : "form",
  );
  const [nlText, setNlText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [nlResult, setNlResult] = useState<{
    confidence?: number;
    suggestions?: string[];
    error?: string;
  } | null>(null);
  // Set when the SQL in the Raw SQL tab was drafted from the English text.
  const [sqlDraft, setSqlDraft] = useState<{
    rationale?: string;
    irError?: string;
    affectedColumns?: string[];
    edited?: boolean;
  } | null>(null);
  // Compile the JSON as soon as a parse produced it, so the SQL is in view.
  const [autoCompile, setAutoCompile] = useState(false);

  const parseNL = async (preferSql = false) => {
    if (!nlText.trim()) {
      setLocalError("Describe the rule in plain English first.");
      return;
    }
    // Without a real profiled schema, NL parsing has nothing to fuzzy-match
    // column/table references against except an unrelated hardcoded demo
    // schema (NSLDS loans/borrowers) — that silently produces rules bound to
    // the wrong table (or no table at all), which is why executing them
    // returns 0 records instead of erroring. Require an active profiling
    // session's schema instead of guessing.
    if (!schemaContext) {
      setNlResult({
        error:
          "No profiled schema available. Open the Rules tab from an active " +
          "Data Profiler session so rule text can be matched against the " +
          "real table/column names — otherwise it may silently bind to the " +
          "wrong table.",
      });
      return;
    }
    setLocalError(null);
    setNlResult(null);
    try {
      setParsing(true);
      const res = await rulesApi.parseNL({
        nl_text: nlText.trim(),
        schema_context: schemaContext,
        project_id: projectId,
        allow_sql: true,
        prefer_sql: preferSql,
        ...(connectionId ? { connection_id: connectionId } : {}),
        ...(sourceKind ? { source_kind: sourceKind } : {}),
      });
      if (!res.success) {
        setNlResult({
          error:
            (res.error_message || "Parse failed") +
            (res.ir_error && res.ir_error !== res.error_message
              ? ` (structured form: ${res.ir_error})`
              : ""),
          suggestions: res.suggestions,
          confidence: res.overall_confidence,
        });
        return;
      }
      // The structured IR can't express this sentence (or SQL was asked
      // for): the server drafted one read-only SELECT. Open it, editable, in
      // the Raw SQL tab; it is saved as a draft like any hand-written rule.
      if (res.kind === "condition_sql" && res.condition_sql) {
        setConditionSql(res.condition_sql);
        if (res.label && !label.trim()) setLabel(res.label);
        if (res.description && !description.trim())
          setDescription(res.description);
        setTableName(res.table_name || "");
        setSqlDraft({
          rationale: res.rationale,
          irError: res.ir_error,
          affectedColumns: res.affected_columns,
        });
        setMode("sql");
        return;
      }
      setSqlDraft(null); // a structured result replaces any earlier SQL draft
      if (res.expression) {
        setExprText(JSON.stringify(res.expression, null, 2));
        setAutoCompile(true);
      }
      // The expression's own rule_type: the label ("Range / Domain") does not
      // map back to its key by lower-casing, so it left the dropdown stale.
      const parsedType =
        typeof res.expression?.rule_type === "string"
          ? res.expression.rule_type
          : res.rule_type_label;
      if (parsedType) {
        const canonical = parsedType.toLowerCase().replace(/ /g, "_");
        const valid: RuleType[] = [
          "conditional",
          "range_domain",
          "referential_integrity",
          "temporal_consistency",
          "cross_table_aggregate",
          "format_pattern",
          "statistical_distribution",
        ];
        if (valid.includes(canonical as RuleType)) {
          setRuleType(canonical as RuleType);
        }
      }
      if (res.description && !description) setDescription(res.description);
      // Derive the table this rule targets from whichever resolved table
      // most of the NL phrase's column bindings agreed on — this is what's
      // persisted as the rule's table_name so it doesn't show "no tables"
      // and actually executes against the right data.
      if (res.column_bindings && res.column_bindings.length > 0) {
        const counts = new Map<string, number>();
        for (const b of res.column_bindings) {
          if (!b.resolved_table) continue;
          counts.set(b.resolved_table, (counts.get(b.resolved_table) ?? 0) + 1);
        }
        let best: string | null = null;
        let bestCount = 0;
        for (const [table, count] of counts) {
          if (count > bestCount) {
            best = table;
            bestCount = count;
          }
        }
        if (best) setTableName(best);
      }
      setNlResult({
        confidence: res.overall_confidence,
        suggestions: res.suggestions,
      });
      // Slide back to form mode so the user can review + edit before saving
      setMode("form");
    } catch (err) {
      setNlResult({
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setParsing(false);
    }
  };

  const submit = async () => {
    setLocalError(null);
    if (!label.trim()) {
      setLocalError("Label is required.");
      return;
    }
    // Raw-SQL rule: create as condition_sql (runs via the existing evaluator).
    if (mode === "sql") {
      if (!conditionSql.trim()) {
        setLocalError("SQL is required for a raw-SQL rule.");
        return;
      }
      try {
        setSubmitting(true);
        await rulesApi.create({
          project_id: projectId,
          label: label.trim(),
          description: description.trim(),
          rule_type: ruleType,
          expr_kind: "condition_sql",
          condition_sql: conditionSql.trim(),
          table_name: tableName || undefined,
          ...(sqlDraft?.affectedColumns?.length
            ? { affected_columns: sqlDraft.affectedColumns }
            : {}),
        });
        onCreated();
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : String(err));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    // Structured rule: parse + submit the DSL expression.
    let expr: Record<string, unknown>;
    try {
      expr = JSON.parse(exprText);
    } catch (e) {
      setLocalError(
        `Invalid JSON: ${e instanceof Error ? e.message : String(e)}`,
      );
      return;
    }
    try {
      setSubmitting(true);
      await rulesApi.create({
        project_id: projectId,
        label: label.trim(),
        description: description.trim(),
        rule_type: ruleType,
        structured_expression: expr,
        input_mode: mode === "nl" || nlText ? "natural_language" : "form",
        nl_text: nlText.trim() || undefined,
        table_name: tableName || undefined,
      });
      onCreated();
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/15 bg-white/5 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white/80">New rule</h3>
        <button
          onClick={onCancel}
          className="text-white/40 hover:text-white/70"
          aria-label="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Authoring mode toggle — form (structured JSON) vs NL (plain English) */}
      <div
        role="tablist"
        aria-label="Authoring mode"
        className="inline-flex rounded-md border border-white/10 overflow-hidden text-xs"
      >
        {(["sql", "form", "nl"] as const).map((m) => (
          <button
            key={m}
            role="tab"
            aria-selected={mode === m}
            onClick={() => setMode(m)}
            className={`px-3 py-1.5 transition-colors ${
              mode === m
                ? "bg-[var(--primary)]/20 text-[var(--primary)]"
                : "text-white/60 hover:text-white/90 hover:bg-white/5"
            }`}
          >
            {m === "form"
              ? "Structured JSON"
              : m === "nl"
                ? "Describe in English"
                : "Raw SQL"}
          </button>
        ))}
      </div>

      {mode === "nl" && (
        <div className="space-y-2">
          <Field label="Describe the rule (plain English)">
            <textarea
              value={nlText}
              onChange={(e) => setNlText(e.target.value)}
              rows={3}
              placeholder={
                describeExample
                  ? `e.g., "${describeExample}"`
                  : "e.g., “For loans, when loan_status is RP, FB, IG, DA, or DL, the outstanding_principal_balance must be greater than zero.”"
              }
              className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-sm text-white/90"
            />
          </Field>
          <div className="flex items-center gap-2">
            <GatedButton
              gate={nlGate}
              onClick={() => void parseNL()}
              disabled={parsing || !nlText.trim()}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[var(--primary)]/20 hover:bg-[var(--primary)]/30 text-[var(--primary)] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {parsing ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Check className="w-3 h-3" />
              )}
              {parsing ? "Parsing…" : "Parse & preview"}
            </GatedButton>
            <GatedButton
              gate={nlGate}
              onClick={() => void parseNL(true)}
              disabled={parsing || !nlText.trim()}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-white/15 text-white/70 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Code2 className="w-3 h-3" />
              Draft as SQL instead
            </GatedButton>
            {nlGate.reason && (
              <span className="text-[11px] text-[var(--warning)]">
                {nlGate.reason}. Use Structured JSON or Raw SQL instead.
              </span>
            )}
            {nlResult?.confidence != null && (
              <span className="text-[11px] text-white/55">
                confidence: {Math.round(nlResult.confidence * 100)}%
              </span>
            )}
          </div>
          {nlResult?.error && (
            <div className="text-xs text-red-300 bg-red-400/10 border border-red-400/30 rounded px-2 py-1.5">
              {nlResult.error}
              {nlResult.suggestions && nlResult.suggestions.length > 0 && (
                <ul className="mt-1 list-disc list-inside text-[11px] text-red-200/80">
                  {nlResult.suggestions.slice(0, 3).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      <Field label="Label">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={
            labelExample || "e.g., NSLDS DQ-6: Open status + zero balance"
          }
          className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-sm text-white/90"
        />
      </Field>
      <Field label="Description (optional)">
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What this rule checks, in plain English"
          className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-sm text-white/90"
        />
      </Field>
      <Field label="Table">
        {schemaContext ? (
          <select
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-sm text-white/90"
          >
            <option value="">— cross-table / not table-specific —</option>
            {Object.keys(schemaContext.tables).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        ) : (
          <input
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            placeholder="Table this rule checks (leave blank if cross-table)"
            className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-sm text-white/90"
          />
        )}
      </Field>
      <Field label="Rule type">
        <select
          value={ruleType}
          onChange={(e) => setRuleType(e.target.value as RuleType)}
          className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-sm text-white/90"
        >
          <option value="conditional">conditional (IF/THEN)</option>
          <option value="range_domain">range_domain</option>
          <option value="referential_integrity">referential_integrity</option>
          <option value="temporal_consistency">temporal_consistency</option>
          <option value="cross_table_aggregate">cross_table_aggregate</option>
          <option value="format_pattern">format_pattern</option>
          <option value="statistical_distribution">
            statistical_distribution
          </option>
        </select>
      </Field>
      {mode === "sql" && sqlDraft && (
        <div
          role="status"
          className="text-xs text-blue-100 bg-blue-400/10 border border-blue-400/30 rounded px-3 py-2 space-y-1"
        >
          <p>
            Drafted as SQL from your description
            {sqlDraft.irError
              ? ": the structured JSON form can't express this rule."
              : "."}{" "}
            {sqlDraft.edited
              ? "You have edited it since; saving checks it again. "
              : "It passed the read-only SQL check. "}
            Review it before saving: it is saved as a draft, and runs in
            profiling only after it is submitted and approved by a Data Steward.
          </p>
          {sqlDraft.rationale && (
            <p className="text-blue-100/70">{sqlDraft.rationale}</p>
          )}
        </div>
      )}
      {mode === "sql" ? (
        <Field label="Violation SQL (returns one row per violation; use {schema})">
          <textarea
            value={conditionSql}
            onChange={(e) => {
              setConditionSql(e.target.value);
              // Edited by the person: the banner's "passed the check" no longer describes this text (saving
              // re-validates it).
              setSqlDraft((d) =>
                d ? { ...d, rationale: undefined, edited: true } : d,
              );
            }}
            rows={10}
            placeholder={
              violationSqlExample
                ? `e.g., ${violationSqlExample}`
                : 'e.g., SELECT * FROM {schema}."candidates" WHERE "gender_reference" = \'Unknown\''
            }
            className="w-full bg-black/40 border border-white/15 rounded px-2 py-2 text-xs text-white/90 font-mono"
          />
        </Field>
      ) : (
        <>
          <Field label="Structured expression (JSON)">
            <textarea
              value={exprText}
              onChange={(e) => {
                setExprText(e.target.value);
                setAutoCompile(false);
              }}
              rows={14}
              className="w-full bg-black/40 border border-white/15 rounded px-2 py-2 text-xs text-white/90 font-mono"
            />
          </Field>
          {!noProjectId(projectId) && (
            <CompiledSqlPreview
              resetKey={exprText}
              autoLoad={autoCompile}
              load={() =>
                rulesApi.compileSql({
                  structured_expression: parseJsonObject(exprText),
                  project_id: projectId,
                  ...(connectionId ? { connection_id: connectionId } : {}),
                  ...(sourceKind ? { source_kind: sourceKind } : {}),
                })
              }
            />
          )}
        </>
      )}
      {localError && <div className="text-xs text-red-300">{localError}</div>}
      <div className="flex items-center justify-end gap-2">
        {createGate.reason && (
          <span className="text-[11px] text-[var(--warning)] mr-auto">
            {createGate.reason}
          </span>
        )}
        <button
          onClick={onCancel}
          className="text-xs px-3 py-1.5 rounded border border-white/15 text-white/70 hover:bg-white/5"
        >
          Cancel
        </button>
        <GatedButton
          gate={createGate}
          onClick={submit}
          disabled={submitting}
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-emerald-500/80 hover:bg-emerald-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Check className="w-3 h-3" />
          )}
          Create rule
        </GatedButton>
      </div>
    </div>
  );
}
